import { qaVerified } from "./qa";
import type { RunStatus } from "./types";

/**
 * THE RUN FLOW — Draft -> Review -> Package, and WHERE THIS RUN ACTUALLY IS.
 *
 * The backend state machine (MASTER_SPEC Part 6) has nineteen states. The operator has
 * three: write the text, decide the edits, export the package. This module is the honest
 * simplification of the one into the other, and it is the ONLY place that mapping exists.
 *
 * TWO RULES, AND EVERY LINE BELOW OBEYS THEM.
 *
 *   1. NOTHING IS INVENTED. Every stage's state is read off the run: the decisions the
 *      operator actually made (change.verdict), the verdict the backend actually returned
 *      (qaLoop.aligned), and the package it actually produced (finalCopy.package). There is
 *      no branch here that infers a pass from a run merely being "approved" — that exact
 *      inference is the bug lib/qa.ts was written to delete, and it is not coming back in
 *      through the nav.
 *
 *   2. IF IT HAS NOT HAPPENED, THE UI SAYS SO. Final QA has not run until the backend
 *      returns a verdict, so until then the Package step is NOT "ready": it is "After QA
 *      passes". A local approval (apiUrl === "", where approveRun records a human decision
 *      and no check executes) says "Recorded locally — no QA". It is never a pass.
 *
 * A QA result computed against a DIFFERENT decision set is not a QA result, so a single
 * undecided edit discards it here exactly as it does on the review screen (useReviewMachine
 * does the same thing with the same predicate). The nav and the review screen can therefore
 * never disagree about where a run is.
 */

export type StageId = "draft" | "review" | "package";

/** done = it happened. current = the run is waiting here. waiting = it has not happened. */
export type StageState = "done" | "current" | "waiting";

export type RunStage = {
  id: StageId;
  step: 1 | 2 | 3;
  label: string;
  /** What this stage IS. Plain English, printed in the menu beside the stage. */
  purpose: string;
  state: StageState;
  /** What has ACTUALLY happened at this stage. Short: it renders on a 320px stepper. */
  status: string;
  /** The route — when the stage IS a destination. Package is not one: the spec has no
   *  package surface, the export happens on the review screen once QA has passed, and a
   *  link to a page that does not exist is a lie about the product's shape. */
  to?: string;
};

export const STAGE_PURPOSE: Record<StageId, string> = {
  draft: "Write and edit the document text.",
  review: "Approve or reject each proposed edit, then run final QA.",
  package: "Export the run — only after all six checks pass."
};

export function runStages(run: RunStatus): RunStage[] {
  const total = run.changes.length;
  const decided = run.changes.filter((change) => change.verdict !== "pending").length;
  const pending = total - decided;

  /** The compiler is still building the draft. Nothing downstream can have happened. */
  const compiling = run.status === "queued" || run.status === "running";
  const reviewComplete = total > 0 && pending === 0;

  /* THE ONLY THREE FACTS THAT CAN MOVE A RUN PAST REVIEW, and each one is read, never
     inferred. qaVerified() is `run.qaLoop?.aligned === true` and nothing else. */
  const qaPassed = reviewComplete && qaVerified(run);
  const qaFailed = reviewComplete && run.qaLoop?.aligned === false;
  /* A human approval with NO check behind it — the supported local-preview path. It is a
     true statement about what happened and it is never dressed up as a pass. */
  const recordedLocally = reviewComplete && !qaPassed && !qaFailed && run.status === "approved";
  const packaged = Boolean(run.finalCopy?.package);

  const sections = run.document.sections.length;

  const draft: RunStage = {
    id: "draft",
    step: 1,
    label: "Draft",
    purpose: STAGE_PURPOSE.draft,
    to: `/runs/${run.runId}/editor`,
    ...(compiling
      ? { state: "current" as const, status: run.status === "queued" ? "Queued" : "Compiling" }
      : total === 0
        ? // The draft exists but nothing has been proposed against it, so there is nothing to
          // review yet. The run is still at the draft.
          { state: "current" as const, status: `${sections} section${sections === 1 ? "" : "s"}` }
        : { state: "done" as const, status: `${sections} section${sections === 1 ? "" : "s"}` })
  };

  const review: RunStage = {
    id: "review",
    step: 2,
    label: "Review",
    purpose: STAGE_PURPOSE.review,
    to: `/runs/${run.runId}/review`,
    ...(compiling
      ? { state: "waiting" as const, status: "After the draft" }
      : total === 0
        ? { state: "waiting" as const, status: "No proposed edits" }
        : pending > 0
          ? { state: "current" as const, status: `${decided} of ${total} decided` }
          : qaPassed
            ? { state: "done" as const, status: "QA passed" }
            : qaFailed
              ? { state: "current" as const, status: "QA did not pass" }
              : recordedLocally
                ? { state: "current" as const, status: "Recorded locally — no QA" }
                : // Every edit is decided and final QA has still not run. Say exactly that.
                  { state: "current" as const, status: "QA not run" })
  };

  const packageStage: RunStage = {
    id: "package",
    step: 3,
    label: "Package",
    purpose: STAGE_PURPOSE.package,
    ...(qaPassed
      ? packaged
        ? { state: "current" as const, status: "Ready to download" }
        : // QA cleared but the backend returned no package. That is not "ready".
          { state: "current" as const, status: "No package returned" }
      : qaFailed
        ? { state: "waiting" as const, status: "Blocked by QA" }
        : { state: "waiting" as const, status: "After QA passes" })
  };

  return [draft, review, packageStage];
}

/** The stage the RUN is waiting at. Exactly one stage is `current` in every branch above. */
export function currentStage(stages: RunStage[]): RunStage {
  return stages.find((stage) => stage.state === "current") || stages[0];
}

/** The run's name. A run without an organization is named by its id, never by a guess. */
export function runName(run: RunStatus) {
  return run.input.organization?.trim() || run.runId;
}
